import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders, deviceModelsSignal } from './assets';
import { consumablesSignal } from './device_models';
import type { components } from '../api/v1';
import { escapeHtml } from '../utils/sanitize';
import { showToast } from '../utils/toast';
import { createDebouncedRunner } from '../utils/debounce';

type Consumable = components['schemas']['Consumable'];
type DeviceModel = components['schemas']['DeviceModel'];
type ConsumablesPageMeta = {
	page: number;
	pageSize: number;
	total: number;
};
type ConsumablesIndexResponse = ConsumablesPageMeta & {
	items: Consumable[];
};

export const selectedConsumableIdSignal = signal<string | null>(null);
export const consumablesSearchQuerySignal = signal<string>('');
export const consumablesPageSignal = signal<number>(1);
export const consumablesPageSizeSignal = signal<number>(20);
export const consumablesPageMetaSignal = signal<ConsumablesPageMeta>({ page: 1, pageSize: 20, total: 0 });
export const consumablesLoadingSignal = signal<boolean>(false);
const runConsumablesSearch = createDebouncedRunner(350);
let consumablesFetchSeq = 0;

export async function refreshConsumablesPage() {
	const fetchSeq = ++consumablesFetchSeq;
	const page = consumablesPageSignal.value;
	const pageSize = consumablesPageSizeSignal.value;
	const search = consumablesSearchQuerySignal.value.trim();
	const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
	if (search) params.set('search', search);

	setConsumablesLoading(true);
	try {
		const res = await fetch(`/api/v1/consumables/index?${params.toString()}`, { headers: getAuthHeaders() });
		if (!res.ok) return;
		const result = await res.json() as ConsumablesIndexResponse;
		if (fetchSeq !== consumablesFetchSeq) return;
		consumablesSignal.value = result.items;
		consumablesPageMetaSignal.value = { page: result.page, pageSize: result.pageSize, total: result.total };
		await clampConsumablesPage();
	} catch (err) {
		console.error('Failed to fetch consumables page', err);
	} finally {
		if (fetchSeq === consumablesFetchSeq) {
			setConsumablesLoading(false);
		}
	}
}

async function clampConsumablesPage() {
	const meta = consumablesPageMetaSignal.value;
	const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));
	if (consumablesPageSignal.value <= totalPages) return;

	consumablesPageSignal.value = totalPages;
	await refreshConsumablesPage();
}

export function renderConsumablesPage(): string {
	const role = activeRoleSignal.peek();
	const isReadOnly = role === 'technician' || role === 'viewer';
	const meta = consumablesPageMetaSignal.peek();
	const consumables = getVisibleConsumablesPageItems(consumablesSignal.peek() || [], meta, consumablesPageSignal.peek());
	const models = deviceModelsSignal.peek() || [];
	const isLoading = consumablesLoadingSignal.peek();
	const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));

	const modelMap = new Map<string, DeviceModel>(models.map((m: DeviceModel) => [m.id, m]));

	const tableRowsHtml = consumables.map(c => {
		const compNames = (c.compatibleModelIds || []).map((id: string) => {
			const m = modelMap.get(id);
			return m ? `${m.vendor} ${m.modelName}` : 'Unknown';
		});

		const badgesHtml = compNames.length === 0
			? '<s-badge tone="attention">No Compatible Models</s-badge>'
			: compNames.map((name: string) => `<s-badge>${escapeHtml(name)}</s-badge>`).join(' ');

		// Dynamic color badge selection
		let badgeTone = 'default';
		if (c.color === 'black') badgeTone = 'attention';
		if (c.color === 'cyan') badgeTone = 'info';
		if (c.color === 'magenta') badgeTone = 'critical';
		if (c.color === 'yellow') badgeTone = 'warning';
		if (c.color === 'none') badgeTone = 'default';

		return `
			<s-table-row>
				<s-table-cell><strong>${escapeHtml(c.name)}</strong></s-table-cell>
				<s-table-cell><code style="font-size:0.85rem;">${escapeHtml(c.code)}</code></s-table-cell>
				<s-table-cell><s-badge tone="${badgeTone}">${escapeHtml(c.color || 'none')}</s-badge></s-table-cell>
				<s-table-cell>${c.printYield.toLocaleString()} pages</s-table-cell>
				<s-table-cell><strong>Rs. ${c.cost.toFixed(2)}</strong></s-table-cell>
				<s-table-cell>
					<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">
						${badgesHtml}
					</div>
				</s-table-cell>
				<s-table-cell>
					${!isReadOnly ? `
						<s-stack direction="inline" gap="small">
							<s-button id="btn-edit-consumable-${c.id}" size="slim">Edit</s-button>
							<s-button id="btn-delete-consumable-${c.id}" tone="critical" size="slim">Delete</s-button>
						</s-stack>
					` : `<s-text tone="subdued">Read-only</s-text>`}
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');



	const isAdminPlus = role === 'superadmin' || role === 'admin';

	return `
		<s-page heading="Consumables Catalog" subtitle="Manage toners, yield rates, and compatible devices." inlineSize="large">
			${!isReadOnly ? `
				<div slot="primaryAction" style="display: flex; gap: 0.5rem; align-items: center;">
					<s-button variant="primary" id="btn-open-create-consumable">Add Consumable</s-button>
${isAdminPlus ? `
					<s-button variant="secondary" id="btn-open-import-consumables">Import CSV</s-button>
`: ''}
				</div>
			` : ''}

			<!-- Search Filter -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<s-stack gap="base">
						<s-text-field 
							label="Search Consumables" 
							id="consumable-search-input" 
							value="${escapeHtml(consumablesSearchQuerySignal.peek())}"
							placeholder="Search by name, code, or color..."
							name="search"
						></s-text-field>
					</s-stack>
				</s-box>
			</s-section>

			<!-- Consumables Table -->
			<s-section>
				<s-box id="consumables-table-region" padding="none" background="surface" border="base" borderRadius="base" style="position:relative;display:block;">
					${renderConsumablesTableRegionHtml(consumables, tableRowsHtml, meta, totalPages, isLoading)}
				</s-box>
			</s-section>

			<!-- Create Consumable Modal -->
			<s-modal id="create-consumable-modal" heading="Create New Consumable">
				<s-form id="form-create-consumable">
					<s-stack gap="base">
						<s-text-field label="Name" name="name" placeholder="e.g. HP 80A Black Toner" required></s-text-field>
						<s-text-field label="Part Code" name="code" placeholder="e.g. CF280A" required></s-text-field>
						
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500; margin-bottom:0.25rem;">Toner Color</label>
							<select name="color" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
								<option value="none">None (Part / Waste Box)</option>
								<option value="black" selected>Black</option>
								<option value="cyan">Cyan</option>
								<option value="magenta">Magenta</option>
								<option value="yellow">Yellow</option>
							</select>
						</div>

						<s-number-field label="Print Yield (pages)" name="printYield" placeholder="e.g. 2500" value="0" required></s-number-field>
						<s-number-field label="Toner Cost (Rs.)" name="cost" placeholder="0.00" value="0.00" step="0.01" required></s-number-field>
						
						<opm-multi-select
							id="create-consumable-models-select"
							label="Compatible Device Models"
							placeholder="Search device models..."
							items='${JSON.stringify(models.map(m => ({ id: m.id, label: `${m.vendor} ${m.modelName}` })))}'
							selected-ids='[]'
						></opm-multi-select>

						<div id="create-consumable-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-consumable">Create Consumable</s-button>
							<s-button commandFor="create-consumable-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit Consumable Modal -->
			<s-modal id="edit-consumable-modal" heading="Edit Consumable Details">
				<s-form id="form-edit-consumable">
					<s-stack gap="base">
						<s-text-field label="Name" name="name" id="edit-consumable-name-input" required></s-text-field>
						<s-text-field label="Part Code" name="code" id="edit-consumable-code-input" required></s-text-field>
						
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500; margin-bottom:0.25rem;">Toner Color</label>
							<select name="color" id="edit-consumable-color-input" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
								<option value="none">None (Part / Waste Box)</option>
								<option value="black">Black</option>
								<option value="cyan">Cyan</option>
								<option value="magenta">Magenta</option>
								<option value="yellow">Yellow</option>
							</select>
						</div>

						<s-number-field label="Print Yield (pages)" name="printYield" id="edit-consumable-yield-input" required></s-number-field>
						<s-number-field label="Toner Cost (Rs.)" name="cost" id="edit-consumable-cost-input" step="0.01" required></s-number-field>
						
						<div id="edit-consumable-models-container">
							<!-- Multi-select populated dynamically -->
						</div>

						<div id="edit-consumable-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-consumable">Save Changes</s-button>
							<s-button commandFor="edit-consumable-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete Consumable Modal -->
			<s-modal id="delete-consumable-modal" heading="Delete Consumable?">
				<s-stack gap="base">
					<s-text tone="critical">Are you sure you want to delete this consumable? Deleting is permanent and will soft-delete the consumable.</s-text>
					<div id="delete-consumable-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-consumable">Delete Consumable</s-button>
						<s-button commandFor="delete-consumable-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Import Consumables Modal -->
			<s-modal id="import-consumables-modal" heading="Import Consumables CSV">
				<s-stack gap="base">
					<s-text tone="subdued">
						Upload a CSV file containing consumable parts. The file must include a header row.
					</s-text>
					<s-box padding="base" background="surface-secondary" border="base" borderRadius="base">
						<s-text style="font-family: monospace;">code,name,cost,color,print_yield<br>CF280A,HP 80A Black,4500.00,black,2700<br>TN-221C,Cyan Toner,7800.00,cyan,1800</s-text>
					</s-box>
					<s-stack direction="inline" gap="small" style="align-items: center;">
						<s-button id="btn-select-consumables-file">Select CSV File</s-button>
						<s-text id="import-consumables-filename" tone="subdued">No file chosen</s-text>
					</s-stack>
					<input type="file" id="input-consumables-csv" accept=".csv" style="display: none;" />
					<div id="import-consumables-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" id="btn-submit-import-consumables">Upload & Import</s-button>
						<s-button commandFor="import-consumables-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupConsumablesPageListeners() {
	updateConsumablesTableRegion();
	const meta = consumablesPageMetaSignal.value;
	const consumables = getVisibleConsumablesPageItems(consumablesSignal.value || [], meta, consumablesPageSignal.value);
	const models = deviceModelsSignal.value || [];

	const searchInput = document.getElementById('consumable-search-input');
	if (searchInput) {
		searchInput.addEventListener('input', (e: Event) => {
			consumablesSearchQuerySignal.value = (e.target as HTMLInputElement).value;
			consumablesPageSignal.value = 1;
			runConsumablesSearch(async () => {
				await refreshConsumablesPage();
				updateConsumablesTableRegion();
			});
		});
	}

	const prevPageBtn = document.getElementById('consumables-prev-page');
	if (prevPageBtn) {
		prevPageBtn.addEventListener('click', async () => {
			consumablesPageSignal.value = Math.max(1, consumablesPageSignal.value - 1);
			await refreshConsumablesPage();
			updateConsumablesTableRegion();
		});
	}

	const nextPageBtn = document.getElementById('consumables-next-page');
	if (nextPageBtn) {
		nextPageBtn.addEventListener('click', async () => {
			const meta = consumablesPageMetaSignal.value;
			const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));
			consumablesPageSignal.value = Math.min(consumablesPageSignal.value + 1, totalPages);
			await refreshConsumablesPage();
			updateConsumablesTableRegion();
		});
	}

	// 1. Open Create Modal
	const openCreateBtn = document.getElementById('btn-open-create-consumable');
	if (openCreateBtn) {
		openCreateBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-consumable-modal') as any;
			const errDiv = document.getElementById('create-consumable-error');
			if (errDiv) errDiv.style.display = 'none';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 2. Submit Create Form
	const submitCreateBtn = document.getElementById('btn-submit-create-consumable');
	if (submitCreateBtn) {
		submitCreateBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-consumable');
			if (!form) return;

			// Gather selected models from multi-select
			const multiSelect = document.getElementById('create-consumable-models-select') as any;
			const checkedModelIds = multiSelect ? multiSelect.selectedIds : [];

			const body = {
				name: (form.querySelector('[name="name"]') as any)?.value || '',
				code: (form.querySelector('[name="code"]') as any)?.value || '',
				color: (form.querySelector('[name="color"]') as any)?.value || 'none',
				printYield: Number((form.querySelector('[name="printYield"]') as any)?.value || 0),
				cost: Number((form.querySelector('[name="cost"]') as any)?.value || 0),
				compatibleModelIds: checkedModelIds,
			};

			const errDiv = document.getElementById('create-consumable-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch('/api/v1/consumables', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('create-consumable-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshConsumablesPage();
					updateConsumablesTableRegion();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create consumable.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 3. Attach Row Edit & Delete Actions
	consumables.forEach(c => {
		const editBtn = document.getElementById(`btn-edit-consumable-${c.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedConsumableIdSignal.value = c.id;

				const nameInput = document.getElementById('edit-consumable-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = c.name;

				const codeInput = document.getElementById('edit-consumable-code-input') as HTMLInputElement;
				if (codeInput) codeInput.value = c.code;

				const colorInput = document.getElementById('edit-consumable-color-input') as HTMLSelectElement;
				if (colorInput) colorInput.value = c.color || 'none';

				const yieldInput = document.getElementById('edit-consumable-yield-input') as HTMLInputElement;
				if (yieldInput) yieldInput.value = c.printYield.toString();

				const costInput = document.getElementById('edit-consumable-cost-input') as HTMLInputElement;
				if (costInput) costInput.value = c.cost.toString();

				const errDiv = document.getElementById('edit-consumable-error');
				if (errDiv) errDiv.style.display = 'none';

				// Render multi-select dynamically
				const container = document.getElementById('edit-consumable-models-container');
				if (container) {
					container.innerHTML = `
						<opm-multi-select
							id="edit-consumable-models-select"
							label="Compatible Device Models"
							placeholder="Search device models..."
							items='${JSON.stringify(models.map(m => ({ id: m.id, label: `${m.vendor} ${m.modelName}` })))}'
							selected-ids='${JSON.stringify(c.compatibleModelIds || [])}'
						></opm-multi-select>
					`;
				}

				const modal = document.getElementById('edit-consumable-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const deleteBtn = document.getElementById(`btn-delete-consumable-${c.id}`);
		if (deleteBtn) {
			deleteBtn.addEventListener('click', () => {
				selectedConsumableIdSignal.value = c.id;
				const errDiv = document.getElementById('delete-consumable-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-consumable-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});

	// 4. Submit Edit Form
	const submitEditBtn = document.getElementById('btn-submit-edit-consumable');
	if (submitEditBtn) {
		submitEditBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedConsumableIdSignal.value;
			if (!id) return;

			const form = document.getElementById('form-edit-consumable');
			if (!form) return;

			// Gather selected models from multi-select
			const multiSelect = document.getElementById('edit-consumable-models-select') as any;
			const checkedModelIds = multiSelect ? multiSelect.selectedIds : [];

			const body = {
				name: (form.querySelector('[name="name"]') as any)?.value || '',
				code: (form.querySelector('[name="code"]') as any)?.value || '',
				color: (form.querySelector('[name="color"]') as any)?.value || 'none',
				printYield: Number((form.querySelector('[name="printYield"]') as any)?.value || 0),
				cost: Number((form.querySelector('[name="cost"]') as any)?.value || 0),
				compatibleModelIds: checkedModelIds,
			};

			const errDiv = document.getElementById('edit-consumable-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/consumables/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('edit-consumable-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshConsumablesPage();
					updateConsumablesTableRegion();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update consumable.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 5. Confirm Delete action
	const confirmDeleteBtn = document.getElementById('btn-confirm-delete-consumable');
	if (confirmDeleteBtn) {
		confirmDeleteBtn.addEventListener('click', async () => {
			const id = selectedConsumableIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-consumable-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/consumables/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-consumable-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshConsumablesPage();
					updateConsumablesTableRegion();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete consumable.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 6. Import CSV Open Button
	const openImportBtn = document.getElementById('btn-open-import-consumables');
	if (openImportBtn) {
		openImportBtn.addEventListener('click', () => {
			const modal = document.getElementById('import-consumables-modal') as any;
			const errDiv = document.getElementById('import-consumables-error');
			if (errDiv) errDiv.style.display = 'none';
			const filenameText = document.getElementById('import-consumables-filename');
			if (filenameText) filenameText.textContent = 'No file chosen';
			const fileInput = document.getElementById('input-consumables-csv') as HTMLInputElement;
			if (fileInput) fileInput.value = '';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 7. Select CSV File Buttons
	const selectFileBtn = document.getElementById('btn-select-consumables-file');
	const fileInput = document.getElementById('input-consumables-csv') as HTMLInputElement;
	if (selectFileBtn && fileInput) {
		selectFileBtn.addEventListener('click', () => {
			fileInput.click();
		});
		fileInput.addEventListener('change', () => {
			const filenameText = document.getElementById('import-consumables-filename');
			if (filenameText) {
				filenameText.textContent = fileInput.files && fileInput.files[0] ? fileInput.files[0].name : 'No file chosen';
			}
		});
	}

	// 8. Submit CSV Upload Form
	const submitImportBtn = document.getElementById('btn-submit-import-consumables');
	if (submitImportBtn) {
		submitImportBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const fileInput = document.getElementById('input-consumables-csv') as HTMLInputElement;
			const errDiv = document.getElementById('import-consumables-error');
			if (errDiv) errDiv.style.display = 'none';

			if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
				if (errDiv) {
					errDiv.textContent = 'Please select a CSV file first.';
					errDiv.style.display = 'block';
				}
				return;
			}

			const file = fileInput.files[0];
			if (!file) return;
			const formData = new FormData();
			formData.append('file', file);

			try {
				const res = await fetch('/api/v1/consumables/import', {
					method: 'POST',
					headers: {
						'Authorization': getAuthHeaders()['Authorization'] || '',
					},
					body: formData,
				});

				if (res.ok) {
					const data = await res.json();
					const modal = document.getElementById('import-consumables-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					consumablesPageSignal.value = 1;
					showToast(`Imported ${data.importedCount} and updated ${data.updatedCount} consumables successfully!`, 'success');
					await refreshConsumablesPage();
					updateConsumablesTableRegion();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to import CSV.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

function renderConsumablesTableRegionHtml(
	consumables: Consumable[],
	tableRowsHtml: string,
	meta: ConsumablesPageMeta,
	totalPages: number,
	isLoading: boolean
): string {
	const loadingOverlayHtml = renderConsumablesLoadingOverlayHtml(isLoading);

	if (consumables.length === 0) {
		return `
			<div style="padding: 3rem; text-align: center;">
				<s-text tone="subdued">No consumables registered in catalog.</s-text>
			</div>
			${loadingOverlayHtml}
		`;
	}

	return `
		<s-table>
			<s-table-header-row>
				<s-table-header>Name</s-table-header>
				<s-table-header>Part Code</s-table-header>
				<s-table-header>Color</s-table-header>
				<s-table-header>Print Yield</s-table-header>
				<s-table-header>Cost</s-table-header>
				<s-table-header>Compatible Models</s-table-header>
				<s-table-header>Actions</s-table-header>
			</s-table-header-row>
			<s-table-body>
				${tableRowsHtml}
			</s-table-body>
		</s-table>
		<s-stack direction="inline" gap="small" style="justify-content: space-between; align-items: center; padding: 1rem;">
			<s-text tone="subdued">Page ${meta.page} of ${totalPages} · ${meta.total} consumables</s-text>
			<div style="display:flex; gap:0.5rem;">
				<s-button id="consumables-prev-page" size="slim" ${meta.page <= 1 ? 'disabled' : ''}>Previous</s-button>
				<s-button id="consumables-next-page" size="slim" ${meta.page >= totalPages ? 'disabled' : ''}>Next</s-button>
			</div>
		</s-stack>
		${loadingOverlayHtml}
	`;
}

function renderConsumablesLoadingOverlayHtml(isLoading: boolean): string {
	return `
		<div id="consumables-loading-spinner" style="display:${isLoading ? 'flex' : 'none'}; position:absolute; inset:0; align-items:center; justify-content:center; background:rgba(255,255,255,0.72); z-index:10; border-radius:4px;">
			<s-stack gap="small" align="center">
				<s-spinner size="large"></s-spinner>
				<s-text tone="subdued">Loading consumables...</s-text>
			</s-stack>
		</div>
	`;
}

function renderConsumablesRowsHtml(consumables: Consumable[], models: DeviceModel[], isReadOnly: boolean): string {
	const modelMap = new Map<string, DeviceModel>(models.map((m: DeviceModel) => [m.id, m]));

	return consumables.map(c => {
		const compNames = (c.compatibleModelIds || []).map((id: string) => {
			const m = modelMap.get(id);
			return m ? `${m.vendor} ${m.modelName}` : 'Unknown';
		});
		const badgesHtml = compNames.length === 0
			? '<s-badge tone="attention">No Compatible Models</s-badge>'
			: compNames.map((name: string) => `<s-badge>${escapeHtml(name)}</s-badge>`).join(' ');

		let badgeTone = 'default';
		if (c.color === 'black') badgeTone = 'attention';
		if (c.color === 'cyan') badgeTone = 'info';
		if (c.color === 'magenta') badgeTone = 'critical';
		if (c.color === 'yellow') badgeTone = 'warning';
		if (c.color === 'none') badgeTone = 'default';

		return `
			<s-table-row>
				<s-table-cell><strong>${escapeHtml(c.name)}</strong></s-table-cell>
				<s-table-cell><code style="font-size:0.85rem;">${escapeHtml(c.code)}</code></s-table-cell>
				<s-table-cell><s-badge tone="${badgeTone}">${escapeHtml(c.color || 'none')}</s-badge></s-table-cell>
				<s-table-cell>${c.printYield.toLocaleString()} pages</s-table-cell>
				<s-table-cell><strong>Rs. ${c.cost.toFixed(2)}</strong></s-table-cell>
				<s-table-cell>
					<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">
						${badgesHtml}
					</div>
				</s-table-cell>
				<s-table-cell>
					${!isReadOnly ? `
						<s-stack direction="inline" gap="small">
							<s-button id="btn-edit-consumable-${c.id}" size="slim">Edit</s-button>
							<s-button id="btn-delete-consumable-${c.id}" tone="critical" size="slim">Delete</s-button>
						</s-stack>
					` : `<s-text tone="subdued">Read-only</s-text>`}
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');
}

function updateConsumablesTableRegion() {
	const region = document.getElementById('consumables-table-region');
	if (!region) return;

	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const meta = consumablesPageMetaSignal.value;
	const consumables = getVisibleConsumablesPageItems(consumablesSignal.value || [], meta, consumablesPageSignal.value);
	const models = deviceModelsSignal.value || [];
	const isLoading = consumablesLoadingSignal.value;
	const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));
	const tableRowsHtml = renderConsumablesRowsHtml(consumables, models, isReadOnly);

	region.innerHTML = renderConsumablesTableRegionHtml(consumables, tableRowsHtml, meta, totalPages, isLoading);
	bindConsumableRowListeners(consumables, models, isReadOnly);
	bindConsumablePaginationListeners();
}

function getVisibleConsumablesPageItems(items: Consumable[], meta: ConsumablesPageMeta, page: number): Consumable[] {
	if (items.length <= meta.pageSize) return items;

	const start = Math.max(0, (Math.max(1, page) - 1) * meta.pageSize);
	return items.slice(start, start + meta.pageSize);
}

function setConsumablesLoading(isLoading: boolean) {
	consumablesLoadingSignal.value = isLoading;
	updateConsumablesLoadingOverlay();
}

function updateConsumablesLoadingOverlay() {
	const spinner = document.getElementById('consumables-loading-spinner');
	if (!spinner) return;
	spinner.style.display = consumablesLoadingSignal.value ? 'flex' : 'none';
}

function bindConsumableRowListeners(consumables: Consumable[], models: DeviceModel[], isReadOnly: boolean) {
	if (isReadOnly) return;

	consumables.forEach(c => {
		const editBtn = document.getElementById(`btn-edit-consumable-${c.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedConsumableIdSignal.value = c.id;

				const nameInput = document.getElementById('edit-consumable-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = c.name;

				const codeInput = document.getElementById('edit-consumable-code-input') as HTMLInputElement;
				if (codeInput) codeInput.value = c.code;

				const colorInput = document.getElementById('edit-consumable-color-input') as HTMLSelectElement;
				if (colorInput) colorInput.value = c.color || 'none';

				const yieldInput = document.getElementById('edit-consumable-yield-input') as HTMLInputElement;
				if (yieldInput) yieldInput.value = c.printYield.toString();

				const costInput = document.getElementById('edit-consumable-cost-input') as HTMLInputElement;
				if (costInput) costInput.value = c.cost.toString();

				const errDiv = document.getElementById('edit-consumable-error');
				if (errDiv) errDiv.style.display = 'none';

				const container = document.getElementById('edit-consumable-models-container');
				if (container) {
					container.innerHTML = `
						<opm-multi-select
							id="edit-consumable-models-select"
							label="Compatible Device Models"
							placeholder="Search device models..."
							items='${JSON.stringify(models.map(m => ({ id: m.id, label: `${m.vendor} ${m.modelName}` })))}'
							selected-ids='${JSON.stringify(c.compatibleModelIds || [])}'
						></opm-multi-select>
					`;
				}

				const modal = document.getElementById('edit-consumable-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const deleteBtn = document.getElementById(`btn-delete-consumable-${c.id}`);
		if (deleteBtn) {
			deleteBtn.addEventListener('click', () => {
				selectedConsumableIdSignal.value = c.id;
				const errDiv = document.getElementById('delete-consumable-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-consumable-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});
}

function bindConsumablePaginationListeners() {
	const prevPageBtn = document.getElementById('consumables-prev-page');
	if (prevPageBtn) {
		prevPageBtn.addEventListener('click', async () => {
			consumablesPageSignal.value = Math.max(1, consumablesPageSignal.value - 1);
			await refreshConsumablesPage();
			updateConsumablesTableRegion();
		});
	}

	const nextPageBtn = document.getElementById('consumables-next-page');
	if (nextPageBtn) {
		nextPageBtn.addEventListener('click', async () => {
			const meta = consumablesPageMetaSignal.value;
			const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));
			consumablesPageSignal.value = Math.min(consumablesPageSignal.value + 1, totalPages);
			await refreshConsumablesPage();
			updateConsumablesTableRegion();
		});
	}
}

export function unmountConsumablesPage() {}
